import { makeVar, useReactiveVar } from '@apollo/client';
import { captureException } from '@sentry/react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import * as monacoEditor from 'monaco-editor';
import { stringify } from 'query-string';
import { useEffect, useState } from 'react';
import {
  languageId,
  registerApolloGraphQLLanguage,
} from 'register-apollo-graphql-language';

import {
  STUDIO_THEME_EDITABLE_DARK,
  studioThemeEditableDark,
} from 'src/components/editor/studioThemeEditableDark';
import {
  STUDIO_THEME_LIGHT,
  studioThemeLight,
} from 'src/components/editor/studioThemeLight';
import {
  TrackingEvent,
  useTrackCustomEvent,
} from 'src/hooks/useTrackCustomEvent';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import config from 'src/lib/config';

import { useThemeContext } from '../../../../components/themeProvider/ThemeProvider';

import { registerGraphQLLanguage } from './registerGraphQLLanguage';
import { registerQueryPlanLanguage } from './registerQueryPlanLanguage';

export const defineStudioMonacoThemes = () => {
  // Setup custom monaco editor theme and json support
  monacoEditor.editor.defineTheme(
    STUDIO_THEME_EDITABLE_DARK,
    studioThemeEditableDark,
  );
  monacoEditor.editor.defineTheme(STUDIO_THEME_LIGHT, studioThemeLight);
};

const themeOverrideReactiveVar = makeVar<
  typeof STUDIO_THEME_EDITABLE_DARK | typeof STUDIO_THEME_LIGHT | null
>(null);
export const useSetMonacoTheme = () => {
  const theme = useThemeContext().themedValue({
    dark: STUDIO_THEME_EDITABLE_DARK,
    light: STUDIO_THEME_LIGHT,
  });
  const themeOverride = useReactiveVar(themeOverrideReactiveVar);
  useEffect(() => {
    monacoEditor.editor.setTheme(themeOverride ?? theme);
  }, [theme, themeOverride]);
};

// Currently we cannot theme space-kit modals, this means we need to force the monaco theme to the light theme.
export const useForceLightMonacoTheme = () => {
  useEffect(() => {
    themeOverrideReactiveVar(STUDIO_THEME_LIGHT);
    return () => {
      themeOverrideReactiveVar(null);
    };
  }, []);
};

export type MonacoLanguage =
  | typeof languageId
  | 'graphql'
  | 'json'
  | 'text'
  | 'markdown'
  | 'html'
  | 'javascript'
  | 'queryPlan'
  | 'yaml';

export const useRegisterMonacoLanguageContribution = (
  language: MonacoLanguage,
) => {
  const ldClient = useLDClient();
  const [ldIsLoading, setLdIsLoading] = useState(true);
  const trackCustomEvent = useTrackCustomEvent();
  useEffect(() => {
    if (ldClient) {
      ldClient.waitUntilReady().then(() => {
        setLdIsLoading(false);
      });
    }
  }, [ldClient]);

  useEffect(() => {
    switch (language) {
      case 'json':
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        require('monaco-editor/esm/vs/language/json/monaco.contribution');
        break;
      case 'html':
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        require('monaco-editor/esm/vs/basic-languages/html/html.contribution');
        break;
      case 'markdown':
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        require('monaco-editor/esm/vs/basic-languages/markdown/markdown.contribution');
        break;
      case 'javascript':
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        require('monaco-editor/esm/vs/language/typescript/monaco.contribution');
        break;
      case 'yaml':
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        require('monaco-editor/esm/vs/basic-languages/yaml/yaml.contribution');
        break;
      case 'graphql':
        registerGraphQLLanguage();
        break;
      case 'queryPlan':
        registerQueryPlanLanguage();
        break;
      case languageId: {
        if (!ldIsLoading) {
          registerApolloGraphQLLanguage({
            version: config.apolloLanguageServerVersion,
            enableDebugLogging: false,
            enableAutoComposition: false,
            forceFederation: true,
            handleWorkerTimeout: () => {
              captureException(new Error('Worker taking a long time to load'));
            },
            onTelemetry: (params) => {
              const analytics = analyticsFromTelemetryParams(params);
              if (analytics) {
                trackCustomEvent(analytics);
              }
            },
            onException: (message, extra) => {
              if (process.env.NODE_ENV === 'production') {
                captureException(new Error(message), { extra });
              } else {
                throw new Error(message);
              }
            },
          });
        }
        break;
      }
      case 'text':
        // nothing to register for 'text'
        break;
      default:
        assertUnreachable(language);
    }
  }, [language, ldIsLoading, trackCustomEvent]);
};

function analyticsFromTelemetryParams(
  params: unknown,
): TrackingEvent<'Language Server'> | null {
  if (isAnalyticsTelemetryEvent(params)) {
    if (analyticsDataIsValid(params.data)) {
      return {
        category: 'Language Server',
        ...params.data,
      };
    } else {
      captureException(
        new Error('Language Server telemetry parameters are of the wrong type'),
      );
    }
  }
  return null;
}

const ANALYTICS_KIND = 'analytics';

interface AnalyticsTelemetryEvent {
  kind: typeof ANALYTICS_KIND;
  data: unknown;
}

// The language server may broadcast various kinds of telemetry; we're only
// interested in the `analytics` kind here.
function isAnalyticsTelemetryEvent(
  event: unknown,
): event is AnalyticsTelemetryEvent {
  return (
    typeof event === 'object' &&
    event !== null &&
    'kind' in event &&
    event.kind === ANALYTICS_KIND &&
    'data' in event
  );
}

// These parameters are coming directly from the language server's telemetry, we
// should ensure they're always what we expect.
function analyticsDataIsValid(
  data: unknown,
): data is Omit<TrackingEvent<'Language Server'>, 'category'> {
  const expectedKeys = new Set(['action', 'label', 'value', 'context']);
  return (
    // it's an actual object AND
    typeof data === 'object' &&
    data !== null &&
    // it has an action which is a string AND
    'action' in data &&
    typeof data.action === 'string' &&
    // the following keys are optional - if they are provided, they're the right type AND
    (!('label' in data) || typeof data.label === 'string') &&
    (!('value' in data) || typeof data.value === 'number') &&
    (!('context' in data) || typeof data.context === 'string') &&
    // it has no other keys
    Object.keys(data).every((key) => expectedKeys.has(key))
  );
}

export interface MonacoFile {
  id: string;
  text: string;
  language: MonacoLanguage;
  subgraphName?: string;
}
export function fileToUri(file: MonacoFile, editorId: string) {
  return monacoEditor.Uri.file(`${editorId}/${file.id}`).with(
    file.subgraphName
      ? // eslint-disable-next-line @typescript-eslint/naming-convention
        { query: stringify({ subgraph_name: file.subgraphName }) }
      : {},
  );
}
export function createNewModel(file: MonacoFile, editorId: string) {
  const newModelUri = fileToUri(file, editorId);

  const oldModel = monacoEditor.editor
    .getModels()
    .find(
      (model) =>
        model.uri.toString(true) === newModelUri.toString(true) &&
        !model?.isDisposed(),
    );
  if (oldModel) {
    oldModel.setValue(file.text);
    monacoEditor.editor.setModelLanguage(oldModel, file.language);
    return oldModel;
  }

  return monacoEditor.editor.createModel(file.text, file.language, newModelUri);
}
